import moment from 'moment';
import get from 'lodash/get';
import { AutoMapper } from './AutoMapper';

const mapper = AutoMapper.getInstance();
const DESTINATION_PREFIX = 'GCC_CPC';

// #region FROM RAW TO VIEW
mapper
  .createMap(`${DESTINATION_PREFIX}_DEFAULT`, `${DESTINATION_PREFIX}_DEFAULT_FORM`)
  .forMember('general.productName', (source) => get(source, 'product.name'))
  .forMember('general.productModel', (source) => get(source, 'product.itemNo'))
  .forMember('general.productDescription', (source) => get(source, 'product.longDescription'))
  .forMember('general.productBarcode', (source) => {
    const barcode = get(source, 'product.shippingInformation.sellingUnit.barcode');
    return barcode;
  })
  .forMember('general.productPictures', (source) => {
    const images = get(source, 'product.files.default.fileArr', []) || [];
    return (
      images
        // .filter((image) => {
        //   if (image) return image.mimeType.includes('image/');
        //   return false;
        // })
        .map((image) => ({
          ...image,
          uid: image.documentListId,
          url: image.url,
          id: image.documentListId,
        }))
    );
  })
  .forMember('general.companyName', (source) => get(source, 'company.companyName'))
  .forMember('general.companyAddress', (source) => {
    const { address, address2, area } = get(source, 'company', {});
    const { city, state, country } = area;
    const addressArr = [address || address2, city, state, country];
    return addressArr.filter((item) => item).join(', ');
  })
  .forMember('general.companyPhone', (source) => {
    const company = get(source, 'company', {});
    const {
      phoneObj = {
        code: '+852',
      },
    } = company;
    return {
      code: phoneObj.code,
      phone: phoneObj.number,
    };
  })
  .forMember('general.contactName', (source) => get(source, 'company.contactPerson'))
  .forMember('general.contactAddress', (source) => get(source, 'company.address'))
  .forMember('general.contactPhone', (source) => {
    const company = get(source, 'company', {});
    const {
      phoneObj = {
        code: '+852',
      },
    } = company;
    return {
      code: phoneObj.code,
      phone: phoneObj.number,
    };
  })
  .forMember('general.contactEmail', (source) => get(source, 'company.contactPersonEmail'))
  .forMember('general.companyLogo', (source) => {
    const companyImage = get(source, 'company.companyImage', null);
    if (companyImage) {
      return [
        {
          ...companyImage,
          id: companyImage.documentListId,
        },
      ];
    }
    return [];
  })
  .forMember('general.companyContactEmail', (source) => get(source, 'company.contactPersonEmail'))
  .forMember('general.manufactureName', (source) => get(source, 'supplier.contactPersonEmail'))
  .forMember('general.manufacturePhone', (source) => {
    const { phoneObj = {} } = get(source, 'supplier', {});
    return {
      code: phoneObj.code || '+852',
      phone: phoneObj.number,
    };
  });

// #endregion

// #region GCC CPC FORM
mapper
  .createMap(`${DESTINATION_PREFIX}_Form`, 'Generate_GCC_CPC')
  .forMember('id', (source) => source.id)
  .forMember('productName', (source) => source.productName)
  .forMember('productModel', (source) => source.productModel)
  .forMember('productBarcode', (source) => source.productBarcode)
  .forMember('productDescription', (source) => source.productDescription)
  .forMember('companyName', (source) => source.companyName)
  .forMember('companyAddress', (source) => source.companyAddress)
  .forMember('companyPhone', (source) => {
    const { code, phone } = source.companyPhone;
    return `${code} ${phone || ''}`;
  })
  .forMember('contactName', (source) => source.contactName)
  .forMember('contactAddress', (source) => source.contactAddress)
  .forMember('contactPhone', (source) => {
    const { code, phone } = source.contactPhone;
    return `${code} ${phone || ''}`;
  })
  .forMember('contactEmail', (source) => source.contactEmail)
  .forMember('manufactureDate', (source) => {
    if (source.manufactureDate) {
      return moment.isMoment(source.manufactureDate)
        ? source.manufactureDate.format('YYYY-MM-DD')
        : source.manufactureDate;
    }
    return null;
  })
  .forMember('manufacturePlace', (source) => source.manufacturePlace)
  .forMember('manufactureName', (source) => source.manufactureName)
  .forMember('manufacturePhone', (source) => {
    const { code, phone } = source.manufacturePhone;
    return `${code} ${phone || ''}`;
  })
  .forMember('remark', (source) => source.remark)
  .forMember('testData', (source) => {
    const { testData } = source;
    if (Array.isArray(testData)) {
      return testData.map((item) => {
        return {
          testItemId: get(item, 'testItemId.value', null),
          citationId: get(item, 'citationId.id', null),
          reportId: item.reportId,
          labName: get(item, 'serviceProvider.name', null),
          expiryDate: moment.isMoment(item.expiryDate)
            ? item.expiryDate.format('YYYY-MM-DD')
            : item.expiryDate,
          testDate: moment.isMoment(item.testDate)
            ? item.testDate.format('YYYY-MM-DD')
            : item.testDate,
        };
      });
    }
    return [];
  })
  .forMember('laboratories', (source) => {
    const { laboratory } = source;
    if (Array.isArray(laboratory)) {
      return laboratory.map((item) => {
        const { phoneObj } = item;
        const { code, phone } = phoneObj;

        return {
          name: item.name,
          phoneNumber: `${code} ${phone || ''}`,
          address: item.address,
          area: item.area,
        };
      });
    }
    return [];
  })
  .forMember('documentListIds', (source) => {
    if (source.productPictures) {
      return source.productPictures.map((item) => item.id);
    }
    return [];
  });

mapper
  .createMap('Generate_GCC_CPC', `${DESTINATION_PREFIX}_Form`)
  .forMember('id', (source) => source.id)
  .forMember('status', (source) => source.status)
  .forMember('general.productName', (source) => source.productName)
  .forMember('general.productModel', (source) => source.productModel)
  .forMember('general.productBarcode', (source) => source.productBarcode)
  .forMember('general.productDescription', (source) => source.productDescription)
  .forMember('general.productPictures', (source) => {
    if (source.documentLists) {
      return source.documentLists.map((documentList) => ({
        ...documentList,
      }));
    }
    return [];
  })
  .forMember('general.companyName', (source) => source.companyName)
  .forMember('general.companyAddress', (source) => source.companyAddress)
  .forMember('general.companyPhone', (source) => {
    if (source.companyPhone) {
      const [code, phone] = source.companyPhone.split(' ');
      return {
        code,
        phone,
      };
    }
    return {
      code: '+852',
      phone: null,
    };
  })
  .forMember('general.contactName', (source) => source.contactName)
  .forMember('general.contactAddress', (source) => source.contactAddress)
  .forMember('general.contactPhone', (source) => {
    if (source.contactPhone) {
      const [code, phone] = source.contactPhone.split(' ');
      return {
        code,
        phone,
      };
    }
    return {
      code: '+852',
      phone: null,
    };
  })
  .forMember('general.contactEmail', (source) => source.contactEmail)
  .forMember('general.manufactureDate', (source) => {
    if (source.manufactureDate) {
      return moment(source.manufactureDate);
    }
    return undefined;
  })
  .forMember('general.manufacturePlace', (source) => source.manufacturePlace)
  .forMember('general.manufactureName', (source) => source.manufactureName)
  .forMember('general.manufacturePhone', (source) => {
    if (source.manufacturePhone) {
      const [code, phone] = source.manufacturePhone.split(' ');
      return {
        code,
        phone,
      };
    }
    return {
      code: '+852',
      phone: null,
    };
  })
  .forMember('general.remark', (source) => source.remark)
  .forMember('testData', (source) => {
    const { testData } = source;
    if (Array.isArray(testData)) {
      return testData.map((item) => {
        return {
          testItemId: item.testItem
            ? {
                value: item.testItem.id,
                label: `${item.testItem.name}, ${get(item, 'citation.name') || ''}`,
                citationList: get(item, 'citation'),
                citationListId: get(item, 'citation.id'),
              }
            : null,
          citationId: item.citation,
          id: item.id,
          serviceProvider: {
            name: item.labName,
          },
          reportId: item.reportId,
          expiryDate: item.expiryDate ? moment(item.expiryDate) : undefined,
          testDate: item.testDate ? moment(item.testDate) : undefined,
        };
      });
    }
    return [];
  })
  .forMember('laboratory', (source) => {
    const { laboratories } = source;
    if (Array.isArray(laboratories)) {
      return laboratories.map((item) => {
        const phoneObj = {
          code: '+852',
          phone: null,
        };
        if (item.phoneNumber) {
          const [code, phone] = item.phoneNumber.split(' ');
          phoneObj.code = code || phoneObj.code;
          phoneObj.phone = phone || phoneObj.phone;
        }

        return {
          name: item.name,
          phoneObj,
          address: item.address,
          area: item.area,
        };
      });
    }
    return [];
  });

// #endregion

// #region DoC FORM

mapper
  .createMap('DoC', `${DESTINATION_PREFIX}_Form`)
  .forMember('id', (source) => source.id)
  .forMember('status', (source) => source.status)
  .forMember('general.companyName', (source) => source.companyName)
  .forMember('general.companyAddress', (source) => source.companyAddress)
  .forMember('general.companyPhone', (source) => {
    if (source.companyPhone) {
      const [code, phone] = source.companyPhone.split(' ');
      return {
        code,
        phone,
      };
    }
    return {
      code: '+852',
      phone: null,
    };
  })
  .forMember('general.companyContactEmail', (source) => source.contactEmail)
  .forMember('general.companyLogo', (source) => {
    if (source.companyLogoDocumentList) {
      return [source.companyLogoDocumentList];
    }
    return [];
  })
  .forMember('general.productName', (source) => source.productName)
  .forMember('general.productModel', (source) => source.productModel)
  .forMember('general.productPictures', (source) => {
    if (source.productDocumentLists) {
      return source.productDocumentLists.map((documentList) => ({
        ...documentList,
      }));
    }
    return [];
  })
  .forMember('general.remark', (source) => source.remark)
  .forMember('testData', (source) => {
    const { testData } = source;
    if (Array.isArray(testData)) {
      return testData.map((item) => {
        return {
          testItemId: item.testItem
            ? {
                value: item.testItem.id,
                label: `${item.testItem.name}, ${get(item, 'citation.name') || ''}`,
                citationList: get(item, 'citation'),
                citationListId: get(item, 'citation.id'),
              }
            : null,
          citationId: item.citation,
          id: item.id,
          serviceProvider: {
            name: item.labName,
          },
          legislation: item.legislation,
        };
      });
    }
    return [];
  })
  .forMember('notifiedBodyInformation', (source) => {
    if (source.notifiedBodyInformation) {
      return {
        serviceProvider: source.notifiedBodyInformation.name,
        notifiedBodyNumber: source.notifiedBodyInformation.notifiedBodyNumber,
        certificateNumber: source.notifiedBodyInformation.certificateNumber,
        description: source.notifiedBodyInformation.descriptionOfIntervention,
      };
    }
    return {};
  });

mapper
  .createMap(`${DESTINATION_PREFIX}_Form`, 'DoC')
  .forMember('id', (source) => source.id)
  .forMember('companyName', (source) => source.companyName)
  .forMember('companyAddress', (source) => source.companyAddress)
  .forMember('companyPhone', (source) => {
    const { code, phone } = source.companyPhone;
    return `${code} ${phone || ''}`;
  })
  .forMember('companyContactEmail', (source) => source.companyContactEmail)
  .forMember('productName', (source) => source.productName)
  .forMember('productModel', (source) => source.productModel)
  .forMember('companyLogoDocumentListId', (source) => {
    if (Array.isArray(source.companyLogo) && source.companyLogo.length > 0) {
      const [logo = {}] = source.companyLogo;
      return logo.id;
    }
    return null;
  })
  .forMember('productDocumentListIds', (source) => {
    if (source.productPictures) {
      return source.productPictures.map((item) => item.id);
    }
    return [];
  })
  .forMember('remark', (source) => source.remark)
  .forMember('testData', (source) => {
    const { testData } = source;
    if (Array.isArray(testData)) {
      return testData.map((item) => {
        return {
          testItemId: get(item, 'testItemId.value', null),
          citationId: get(item, 'citationId.id', null),
          labName: get(item, 'serviceProvider.name', null),
          legislation: item.legislation,
        };
      });
    }
    return [];
  })
  .forMember('notifiedBodyInformation', (source) => {
    const { notifiedBodyInformation } = source;
    return {
      notifiedBodyName: get(notifiedBodyInformation, 'serviceProvider') || '',
      notifiedBodyNumber: get(notifiedBodyInformation, 'notifiedBodyNumber') || '',
      notifiedCertificateNumber: get(notifiedBodyInformation, 'certificateNumber') || '',
      descriptionOfIntervention: get(notifiedBodyInformation, 'description') || '',
    };
  });

// #endregion

// #region SIGN OFF FORM
mapper
  .createMap('DoC', `${DESTINATION_PREFIX}_SIGN_OFF_FORM`)
  .forMember('id', (source) => source.id)
  .forMember('name', (source) => source.name)
  .forMember('title', (source) => source.title)
  .forMember('issueDate', (source) => {
    if (source.dateOfIssue) {
      return moment(source.dateOfIssue);
    }
    return undefined;
  })
  .forMember('issuePlace', (source) => source.placeOfIssue)
  .forMember('signatureDate', (source) => {
    if (source.dateOfSignature) {
      return moment(source.dateOfSignature);
    }
    return undefined;
  })
  .forMember('signatureFiles', (source) => {
    if (source.documentList) {
      return [source.documentList];
    }
    return [];
  });

mapper
  .createMap(`${DESTINATION_PREFIX}_SIGN_OFF_FORM`, 'DoC')
  .forMember('id', (source) => source.id)
  .forMember('name', (source) => source.name)
  .forMember('title', (source) => source.title)
  .forMember('dateOfIssue', (source) => {
    if (source.issueDate) {
      return source.issueDate.format('YYYY-MM-DD');
    }
    return null;
  })
  .forMember('placeOfIssue', (source) => source.issuePlace)
  .forMember('documentListId', (source) => {
    if (source.signatureFiles) {
      const [signature] = source.signatureFiles;
      return signature.id;
    }
    return null;
  })
  .forMember('dateOfSignature', (source) => {
    if (source.signatureDate) {
      return source.signatureDate.format('YYYY-MM-DD');
    }
    return undefined;
  });
// #endregion

export const GccCpcMappings = {
  mapInitialValuesToForm: (source) => {
    const transformedForm = mapper.map(
      source,
      `${DESTINATION_PREFIX}_DEFAULT`,
      `${DESTINATION_PREFIX}_DEFAULT_FORM`
    );
    return transformedForm;
  },
  transformGCCFormToAPI: (form) => {
    const transformedForm = mapper.map(form, `${DESTINATION_PREFIX}_Form`, 'Generate_GCC_CPC');
    return transformedForm;
  },
  parseGCCFormFromAPI: (form) => {
    const parsedForm = mapper.map(form, 'Generate_GCC_CPC', `${DESTINATION_PREFIX}_Form`);
    return parsedForm;
  },
  parseDoCFormFromAPI: (form) => {
    const parsedForm = mapper.map(form, 'DoC', `${DESTINATION_PREFIX}_Form`);
    return parsedForm;
  },
  transformDoCFormToAPI: (form) => {
    const transformedForm = mapper.map(form, `${DESTINATION_PREFIX}_Form`, 'DoC');
    return transformedForm;
  },
  parseSignOffFromAPI: (form) => {
    return mapper.map(form, 'DoC', `${DESTINATION_PREFIX}_SIGN_OFF_FORM`);
  },
  transformSignOffToAPI: (form) => {
    return mapper.map(form, `${DESTINATION_PREFIX}_SIGN_OFF_FORM`, 'DoC');
  },
};
